import * as React from 'react'
import { Link, useParams } from 'react-router-dom'
import clx from 'classnames'
import { useQuerySubscriptionStatus } from '../api/useQuerySubscriptionStatus'
import { useQuerySubscriptionUsage } from '../api/useQuerySubscriptionUsage'
import { useQueryInvoices } from '../api/useQueryInvoices'
import { useQueryPanelInvoices } from '../api/useQueryPanelInvoices'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { axiosInstance } from '@/lib/axios'
import { Button } from '@/ui/button'
import { SettingsSectionWithTabs } from '@/modules/settings/components/SettingsSectionWithTabs'
import { usePersistentTab } from '@/modules/shared/stores/tabsStore'
import { formatPanelDate } from '@/modules/shared/utils/panelInvoices'
import { useQueryCompanies } from '@/modules/shared/query-client/useQueryCompanies'
import { CompanyType } from '@/app/constants/companyType'
import { useUmbrellaAccount } from '@/modules/shared/hooks/useUmbrellaAccount'
import { UmbrellaBillingNotice } from '@/modules/shared/components/serviceAccount/UmbrellaBillingNotice'

const Th: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <th className="tw-px-1 tw-py-2 tw-font-medium tw-text-gray-500">{children}</th>
)

const StatusPill: React.FC<{ ok: boolean; label: string }> = ({
  ok,
  label,
}) => (
  <span
    className={clx(
      'tw-rounded tw-px-2 tw-py-0.5 tw-text-xs',
      ok
        ? 'tw-bg-green-100 tw-text-green-800'
        : 'tw-bg-amber-100 tw-text-amber-800',
    )}
  >
    {label}
  </span>
)

// The panel could not be reached — the invoice data is stale/absent, not empty.
const PanelDegradedNotice: React.FC = () => {
  const { t } = useLocalize()
  return (
    <div className="tw-rounded-md tw-border tw-border-amber-300 tw-bg-amber-100 tw-p-3.5 tw-text-sm tw-text-amber-800">
      {t('subscription.profile.panelDegraded')}
    </div>
  )
}

// Shown when invoices are issued to the service account (konto usługowe), not
// to the selected company itself.
const ServiceAccountNote: React.FC<{ name: string }> = ({ name }) => {
  const { t } = useLocalize()
  return (
    <p className="tw-m-0 tw-text-sm tw-text-gray-500">
      {t('subscription.profile.panelServiceAccount', { name })}
    </p>
  )
}

const downloadProforma = async (url: string, fileName: string) => {
  const res = await axiosInstance.get(url, { responseType: 'blob' })
  const blobUrl = window.URL.createObjectURL(
    new Blob([res.data], { type: 'application/pdf' }),
  )
  const a = document.createElement('a')
  a.href = blobUrl
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  a.remove()
  window.URL.revokeObjectURL(blobUrl)
}

type Props = {
  // Tylko dla /profile (bez firmy w URL) — tam pokazujemy macierzystą firmę z tenant/me.
  companyId?: string
}

export const MySubscriptionPage: React.FC<Props> = (props) => {
  const { t } = useLocalize()
  // Everything on this page is keyed by the company in the URL. Do NOT use
  // tenant/me here: it returns the *viewer's own* home tenant, which is a
  // different company whenever an admin or multi-company user opens another
  // company's subscription page.
  const params = useParams<{ companyId: string }>()
  const companyId = params.companyId ?? props.companyId
  const {
    data: status,
    isLoading: statusLoading,
    error: statusError,
  } = useQuerySubscriptionStatus(companyId)
  // Konto usługowe nie kupuje pakietu dla siebie — jest rozliczane na NIP konta,
  // więc zamiast „wybierz pakiet" dostaje wyjaśnienie.
  const { data: companies } = useQueryCompanies({ includeDeleted: false })
  const isServiceCompany =
    companies?.find((c) => String(c.id) === String(companyId))?.type ===
    CompanyType.SERVICE
  // Manual (Claro-invoiced) companies have no P24 proformas — their invoices
  // live in the external payment panel, so the two invoice queries are
  // exclusive. Both wait for status so the provider decides what we fetch.
  const isManual = status?.billingProvider === 'Manual'
  const { data: usage } = useQuerySubscriptionUsage(companyId)
  const { data: invoices } = useQueryInvoices(
    status && !isManual ? companyId : null,
  )
  const { data: panelInvoices, isLoading: panelLoading } =
    useQueryPanelInvoices(companyId, isManual)

  const [selectedTab, setSelectedTab] = usePersistentTab('subscription', 'a')
  const { umbrella, isLoading: umbrellaLoading } = useUmbrellaAccount(companyId)

  // A child of a service account has no billing of its own: its row's plan/expiry is
  // meaningless and the panel invoices belong to the account.
  if (umbrella) return <UmbrellaBillingNotice companyId={companyId!} />

  if (statusLoading || umbrellaLoading) {
    return (
      <div className="tw-flex tw-flex-1 tw-items-center tw-px-6 tw-py-8 tw-text-gray-600">
        {t('subscription.loading')}
      </div>
    )
  }

  // Zapytanie padło (403 — brak uprawnień do tej firmy, 5xx, brak sieci). To NIE
  // znaczy „nie masz konta firmowego": ta sama treść dla błędu myliła klientów,
  // którzy widzieli propozycję zakupu pakietu na firmie, którą już mają. 404 idzie
  // niżej — tam brak subskrypcji jest prawdziwą odpowiedzią, nie awarią.
  const httpStatus = (statusError as any)?.response?.status
  if (statusError && httpStatus !== 404) {
    return (
      <div className="tw-flex tw-flex-1 tw-items-center tw-px-6 tw-py-8 tw-text-gray-600">
        {httpStatus === 403
          ? t('subscription.profile.statusForbidden')
          : t('subscription.profile.statusError')}
      </div>
    )
  }

  // Status 404s when the company has no configuration record (never migrated /
  // never bootstrapped) — there is no subscription to show.
  if (!status) {
    return (
      <div className="tw-flex tw-flex-1 tw-items-center tw-px-6 tw-py-8 tw-text-gray-600">
        {isServiceCompany ? (
          t('subscription.profile.serviceCompanyNoPlan')
        ) : (
          <>
            {t('subscription.profile.noCompany')}{' '}
            {/* Bez companyId picker bierze `tenant/me`, czyli macierzystą firmę
                widza — pakiet kupiłby się innej firmie niż ta na ekranie. */}
            <Link
              to={companyId ? `/checkout?companyId=${companyId}` : '/post-register'}
              className="tw-ml-1 tw-text-primary tw-underline"
            >
              {t('subscription.profile.choosePlan')}
            </Link>
            .
          </>
        )}
      </div>
    )
  }

  const expiresLabel = status?.expiresAt
    ? new Date(status.expiresAt).toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '—'

  const usagePercent =
    usage && usage.includedDrivers > 0
      ? Math.min(100, (usage.billedDriverCount / usage.includedDrivers) * 100)
      : 0

  // For Manual companies the panel verdict is the only real status — the local
  // SubscriptionExpirationDate is not maintained for them, so it reads as
  // inactive. Until the panel answers (or when it can't be reached) we know
  // nothing and render no badge; falling back to the local flag flashed a wrong
  // "nieaktywna" on slow connections.
  const panelStatus = isManual ? panelInvoices?.panelStatus : null
  const badgeState: 'unpaid' | 'active' | 'inactive' | null = isManual
    ? panelStatus === 'unpaid' || panelStatus === 'not_in_panel'
      ? 'unpaid'
      : panelStatus === 'paid'
        ? 'active'
        : null
    : status.active
      ? 'active'
      : 'inactive'
  const statusBadge = badgeState && (
    <span
      className={clx(
        'tw-inline-block tw-rounded-full tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold',
        badgeState === 'active'
          ? 'tw-bg-green-100 tw-text-green-800'
          : 'tw-bg-red-100 tw-text-red-700',
      )}
    >
      {badgeState === 'unpaid'
        ? t('subscription.profile.unpaidBadge')
        : t(`subscription.profile.${badgeState}`)}
    </span>
  )

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      <div className="tw-flex tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
        <SettingsSectionWithTabs
          className="tw-mb-2"
          selected={selectedTab}
          setSelected={setSelectedTab}
        >
          <div
            data-tab-name="a"
            title={t('subscription.profile.tabSubscription')}
            className="tw-flex tw-max-w-[640px] tw-flex-col tw-gap-8 tw-p-2 tw-pb-8"
          >
            <div className="tw-flex tw-flex-col tw-gap-3">
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
                <div>
                  <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-gray-500">
                    {isManual
                      ? t('subscription.profile.billingTitle')
                      : t('subscription.profile.currentPlan')}
                  </div>
                  <div className="tw-mt-1 tw-text-2xl tw-font-bold">
                    {isManual
                      ? (panelInvoices?.panelCompanyName ??
                        status.companyName ??
                        '—')
                      : (status.planCode ?? '—')}
                  </div>
                </div>
                {statusBadge}
              </div>

              <dl className="tw-grid tw-grid-cols-[180px_1fr] tw-gap-2 tw-text-sm">
                {isManual ? (
                  <>
                    <dt className="tw-text-gray-500">
                      {t('subscription.profile.billingMethod')}
                    </dt>
                    <dd>{t('subscription.profile.manualInvoices')}</dd>
                    {panelInvoices?.nip && (
                      <>
                        <dt className="tw-text-gray-500">
                          {t('subscription.profile.panelNip')}
                        </dt>
                        <dd>{panelInvoices.nip}</dd>
                      </>
                    )}
                    {panelInvoices && panelInvoices.invoices.length > 0 && (
                      <>
                        <dt className="tw-text-gray-500">
                          {t('subscription.profile.panelInvoiceCount')}
                        </dt>
                        <dd>{panelInvoices.invoices.length}</dd>
                        <dt className="tw-text-gray-500">
                          {t('subscription.profile.panelLastInvoice')}
                        </dt>
                        <dd>
                          {panelInvoices.invoices[0].number}
                          <span className="tw-text-gray-500">
                            {' · '}
                            {formatPanelDate(
                              panelInvoices.invoices[0].issueDate,
                            )}
                          </span>
                        </dd>
                      </>
                    )}
                    <dt className="tw-text-gray-500">
                      {t('subscription.profile.panelOutstanding')}
                    </dt>
                    <dd
                      className={clx({
                        'tw-font-semibold tw-text-red-700':
                          panelStatus === 'unpaid',
                      })}
                    >
                      {panelLoading
                        ? '…'
                        : panelStatus === 'unpaid'
                          ? (panelInvoices?.sum ?? '—')
                          : panelStatus === 'paid'
                            ? t('subscription.profile.panelNoOutstanding')
                            : '—'}
                    </dd>
                    {status.expiresAt && (
                      <>
                        <dt className="tw-text-gray-500">
                          {t('subscription.profile.expires')}
                        </dt>
                        <dd>
                          {t('subscription.profile.expiresOn', {
                            date: expiresLabel,
                          })}
                        </dd>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <dt className="tw-text-gray-500">
                      {t('subscription.profile.billingModeLabel')}
                    </dt>
                    <dd>
                      {status?.billingMode === 'MonthlyCard' &&
                        t('subscription.profile.modeMonthlyCard')}
                      {status?.billingMode === 'Prepaid6m' &&
                        t('subscription.profile.modePrepaid6m')}
                      {status?.billingMode === 'Prepaid12m' &&
                        t('subscription.profile.modePrepaid12m')}
                      {!status?.billingMode || status.billingMode === 'None'
                        ? '—'
                        : null}
                    </dd>
                    <dt className="tw-text-gray-500">
                      {status?.autoRenew
                        ? t('subscription.profile.nextRenewal')
                        : t('subscription.profile.expires')}
                    </dt>
                    <dd>
                      {status?.autoRenew
                        ? t('subscription.profile.autoRenewOn', {
                            date: expiresLabel,
                          })
                        : t('subscription.profile.expiresOn', {
                            date: expiresLabel,
                          })}
                    </dd>
                  </>
                )}
              </dl>

              {isManual ? (
                <>
                  {panelStatus === 'error' && <PanelDegradedNotice />}
                  {panelStatus === 'unpaid' && (
                    <div className="tw-rounded-md tw-border tw-border-red-300 tw-bg-red-100 tw-p-3.5 tw-text-sm tw-text-red-800">
                      {t('subscription.profile.panelUnpaidNotice', {
                        sum: panelInvoices?.sum ?? '—',
                      })}
                    </div>
                  )}
                  {panelStatus === 'not_in_panel' && (
                    <div className="tw-rounded-md tw-border tw-border-amber-300 tw-bg-amber-100 tw-p-3.5 tw-text-sm tw-text-amber-800">
                      {t('subscription.profile.panelNotInPanel')}
                    </div>
                  )}
                  {panelInvoices?.serviceAccountName && (
                    <ServiceAccountNote name={panelInvoices.serviceAccountName} />
                  )}
                  <div className="tw-rounded-md tw-border tw-border-base-300 tw-bg-base-200 tw-p-3.5 tw-text-sm tw-text-gray-700">
                    {t('subscription.profile.manualContact')}
                  </div>
                </>
              ) : status?.canManageBilling ? (
                <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                  <Link to="/checkout">
                    <Button
                      type="button"
                      size="sm"
                      text={
                        status?.active
                          ? t('subscription.profile.changePlan')
                          : t('subscription.profile.choosePlanBtn')
                      }
                    />
                  </Link>
                  <span className="tw-text-sm tw-text-gray-500">
                    {t('subscription.profile.oneTimePayment')}
                  </span>
                </div>
              ) : (
                <p className="tw-m-0 tw-text-sm tw-text-gray-500">
                  {t('subscription.profile.contactAdmin')}
                </p>
              )}
            </div>

            {usage && usage.includedDrivers > 0 && (
              <div className="tw-flex tw-flex-col tw-gap-3 tw-border-t tw-border-base-300 tw-pt-6">
                <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-4">
                  <div>
                    <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-gray-500">
                      {t('subscription.profile.usageTitle')}
                    </div>
                    <div className="tw-mt-1 tw-text-2xl tw-font-bold">
                      {usage.billedDriverCount} / {usage.includedDrivers}
                      <span className="tw-ml-2 tw-text-sm tw-font-normal tw-text-gray-500">
                        {t('subscription.profile.drivers')}
                      </span>
                    </div>
                    <div className="tw-mt-1 tw-text-sm tw-text-gray-500">
                      {t('subscription.profile.usageBasis')}
                    </div>
                  </div>
                  {usage.isOverLimit && (
                    <div className="tw-max-w-[320px] tw-rounded-md tw-border tw-border-red-300 tw-bg-red-100 tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-red-800">
                      {t('subscription.profile.overLimit', {
                        count: usage.overageDrivers,
                        unit:
                          usage.overageDrivers === 1
                            ? t('subscription.profile.driverAccOne')
                            : t('subscription.profile.driverAccMany'),
                      })}
                    </div>
                  )}
                </div>
                <div className="tw-h-2 tw-w-full tw-overflow-hidden tw-rounded tw-bg-base-300">
                  <div
                    className={clx('tw-h-full tw-transition-all', {
                      'tw-bg-red-600': usage.isOverLimit,
                      'tw-bg-amber-500':
                        !usage.isOverLimit && usagePercent > 80,
                      'tw-bg-primary':
                        !usage.isOverLimit && usagePercent <= 80,
                    })}
                    style={{ width: `${usagePercent}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <div
            data-tab-name="b"
            title={t('subscription.profile.tabInvoices')}
            className="tw-flex tw-flex-col tw-gap-4 tw-p-2 tw-pb-8"
          >
            {isManual ? (
              <>
                {panelStatus === 'error' && <PanelDegradedNotice />}
                {panelInvoices?.serviceAccountName && (
                  <ServiceAccountNote name={panelInvoices.serviceAccountName} />
                )}
                {panelInvoices?.invoices.length ? (
                  <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-border-collapse tw-text-sm">
                      <thead>
                        <tr className="tw-border-b tw-border-base-300 tw-text-left">
                          <Th>{t('subscription.profile.colNumber')}</Th>
                          <Th>{t('subscription.profile.colIssued')}</Th>
                          <Th>{t('subscription.profile.colDueDate')}</Th>
                          <Th>{t('subscription.profile.colAmount')}</Th>
                          <Th>{t('subscription.profile.colLeftToPay')}</Th>
                          <Th>{t('subscription.profile.colStatus')}</Th>
                          <th className="tw-px-1 tw-py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {panelInvoices.invoices.map((inv) => {
                          const formatAmount = (v: number) =>
                            v.toLocaleString('pl-PL', {
                              style: 'currency',
                              currency: inv.currency || 'PLN',
                            })
                          return (
                            <tr
                              key={inv.number}
                              className="tw-border-b tw-border-base-200"
                            >
                              <td className="tw-px-1 tw-py-2">
                                {inv.number}
                                {inv.ksefNumber && (
                                  <span
                                    className="tw-block tw-text-xs tw-text-gray-500"
                                    title={inv.ksefNumber}
                                  >
                                    KSeF: {inv.ksefNumber}
                                  </span>
                                )}
                              </td>
                              <td className="tw-px-1 tw-py-2">
                                {formatPanelDate(inv.issueDate)}
                              </td>
                              <td
                                className={clx('tw-px-1 tw-py-2', {
                                  'tw-text-red-700': !inv.isSettled,
                                })}
                              >
                                {formatPanelDate(inv.dueDate)}
                              </td>
                              <td className="tw-px-1 tw-py-2">
                                {formatAmount(inv.amountToPay)}
                              </td>
                              <td className="tw-px-1 tw-py-2">
                                {formatAmount(inv.amountLeft)}
                              </td>
                              <td className="tw-px-1 tw-py-2">
                                <StatusPill
                                  ok={inv.isSettled}
                                  label={
                                    inv.isSettled
                                      ? t('subscription.profile.statusPaid')
                                      : t('subscription.profile.statusUnpaid')
                                  }
                                />
                              </td>
                              <td className="tw-px-1 tw-py-2 tw-text-right">
                                {inv.invoiceUrl && (
                                  <a
                                    href={inv.invoiceUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <Button
                                      type="button"
                                      size="sm"
                                      text={t('subscription.profile.document')}
                                    />
                                  </a>
                                )}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                    {panelInvoices.sum && (
                      <div className="tw-mt-3 tw-flex tw-items-baseline tw-justify-between tw-px-1">
                        <span className="tw-text-sm tw-font-medium tw-text-gray-500">
                          {t('subscription.profile.sumOutstanding')}
                        </span>
                        <span className="tw-text-base tw-font-bold">
                          {panelInvoices.sum}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="tw-m-0 tw-text-sm tw-text-gray-500">
                    {panelLoading
                      ? t('subscription.loading')
                      : panelStatus === 'paid'
                        ? t('subscription.profile.panelPaidNotice')
                        : t('subscription.profile.noInvoices')}
                  </p>
                )}
              </>
            ) : invoices && invoices.length > 0 ? (
              <div className="tw-overflow-x-auto">
                <table className="tw-w-full tw-border-collapse tw-text-sm">
                  <thead>
                    <tr className="tw-border-b tw-border-base-300 tw-text-left">
                      <Th>{t('subscription.profile.colDate')}</Th>
                      <Th>{t('subscription.profile.colNumber')}</Th>
                      <Th>{t('subscription.profile.colAmount')}</Th>
                      <Th>{t('subscription.profile.colStatus')}</Th>
                      <th className="tw-px-1 tw-py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr
                        key={inv.id}
                        className="tw-border-b tw-border-base-200"
                      >
                        <td className="tw-px-1 tw-py-2">
                          {new Date(inv.created).toLocaleDateString('pl-PL')}
                        </td>
                        <td className="tw-px-1 tw-py-2">{inv.number ?? '—'}</td>
                        <td className="tw-px-1 tw-py-2">
                          {inv.amountPaid.toLocaleString('pl-PL', {
                            style: 'currency',
                            currency: inv.currency || 'PLN',
                          })}
                        </td>
                        <td className="tw-px-1 tw-py-2">
                          <StatusPill
                            ok={inv.status === 'paid'}
                            label={inv.status}
                          />
                        </td>
                        <td className="tw-px-1 tw-py-2 tw-text-right">
                          {inv.invoicePdfUrl && (
                            <button
                              type="button"
                              onClick={() =>
                                downloadProforma(
                                  inv.invoicePdfUrl!,
                                  `${(inv.number ?? 'proforma').replace(
                                    /\//g,
                                    '_',
                                  )}.pdf`,
                                )
                              }
                              className="tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0 tw-text-primary tw-underline"
                            >
                              PDF
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="tw-m-0 tw-text-sm tw-text-gray-500">
                {t('subscription.profile.noInvoices')}
              </p>
            )}
          </div>
        </SettingsSectionWithTabs>
      </div>
    </div>
  )
}
